import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Snackbar,
  Grid,
  Tabs,
  Tab,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Palette as ThemeIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Visibility as PreviewIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  ColorLens as ColorIcon,
  Animation as AnimationIcon,
  EmojiEmotions as DecorationIcon,
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { adminThemesApi } from '../../api';
import type { BoardTheme, ThemeCategory, ThemeData, CreateThemeInput } from '../../types';
import { THEME_CATEGORIES, DEFAULT_THEME_DATA } from '../../types';
import { ThemePreview, ThemeWrapper, ThemeFullPreview } from '../../components/theme';
import { useDocumentTitle } from '../../hooks/core';
import { TabPanel, ColorInput, DecorationEditor, ANIMATION_OPTIONS, TEXT_COLOR_MODE_OPTIONS } from '../../components/admin/themes';

// Admin theme type includes usage stats
interface ThemeWithStats extends BoardTheme {
  ownerCount: number;
  boardCount: number;
}

export function ThemesPage() {
  useDocumentTitle('Themes');

  const [themes, setThemes] = useState<ThemeWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<ThemeCategory | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Preview dialog
  const [previewTheme, setPreviewTheme] = useState<ThemeWithStats | null>(null);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTheme, setDeleteTheme] = useState<ThemeWithStats | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editTheme, setEditTheme] = useState<ThemeWithStats | null>(null);
  const [editTab, setEditTab] = useState(0);
  const [editInput, setEditInput] = useState<{
    name?: string;
    description?: string;
    category?: ThemeCategory;
    creditPrice?: number;
    isFree?: boolean;
    active?: boolean;
    displayOrder?: number;
    themeData?: ThemeData;
  }>({});

  // Create dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createInput, setCreateInput] = useState<CreateThemeInput>({
    lookupKey: '',
    name: '',
    description: '',
    category: 'classic' as ThemeCategory,
    creditPrice: 1,
    isFree: false,
    displayOrder: 100,
    active: true,
    themeData: { ...DEFAULT_THEME_DATA },
  });

  useEffect(() => {
    fetchThemes();
  }, []);

  const fetchThemes = async () => {
    try {
      setLoading(true);
      const data = await adminThemesApi.listAllThemes();
      setThemes(data.themes as ThemeWithStats[]);
      setError(null);
    } catch (err) {
      setError('Failed to load themes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (theme: ThemeWithStats) => {
    setEditTheme(theme);
    setEditInput({
      name: theme.name,
      description: theme.description,
      category: theme.category,
      creditPrice: theme.creditPrice,
      isFree: theme.isFree,
      active: theme.active,
      displayOrder: theme.displayOrder,
      themeData: theme.themeData ? { ...theme.themeData } : { ...DEFAULT_THEME_DATA },
    });
    setEditTab(0);
    setEditDialogOpen(true);
  };

  const handleEditSave = async (closeAfterSave = false) => {
    if (!editTheme) return;

    try {
      setEditLoading(true);
      setEditError(null);
      await adminThemesApi.updateTheme(editTheme.id, editInput);
      setSuccess('Theme updated successfully');
      if (closeAfterSave) {
        setEditDialogOpen(false);
      }
      fetchThemes();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update theme';
      setEditError(errorMessage);
    } finally {
      setEditLoading(false);
    }
  };

  const handleCreateOpen = () => {
    setCreateInput({
      lookupKey: '',
      name: '',
      description: '',
      category: 'classic' as ThemeCategory,
      creditPrice: 1,
      isFree: false,
      displayOrder: 100,
      active: true,
      themeData: { ...DEFAULT_THEME_DATA },
    });
    setCreateError(null);
    setCreateDialogOpen(true);
  };

  const handleCreateSave = async () => {
    if (!createInput.lookupKey || !createInput.name) {
      setCreateError('Lookup key and name are required');
      return;
    }

    try {
      setCreateLoading(true);
      setCreateError(null);
      await adminThemesApi.createTheme(createInput);
      setSuccess('Theme created successfully');
      setCreateDialogOpen(false);
      fetchThemes();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create theme';
      setCreateError(errorMessage);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteClick = (theme: ThemeWithStats) => {
    setDeleteTheme(theme);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTheme) return;

    try {
      setDeleteLoading(true);
      await adminThemesApi.deleteTheme(deleteTheme.id);
      setSuccess(`Theme "${deleteTheme.name}" deleted successfully`);
      setDeleteDialogOpen(false);
      setDeleteTheme(null);
      fetchThemes();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete theme';
      setError(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Import theme data into the edit form (for updating existing theme)
  const handleImportToEdit = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !editTheme) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target?.result as string);

        // Update the edit input with imported values (preserve lookupKey and ID)
        setEditInput((prev) => ({
          ...prev,
          name: importedData.name ?? prev.name,
          description: importedData.description ?? prev.description,
          category: importedData.category ?? prev.category,
          creditPrice: importedData.creditPrice ?? prev.creditPrice,
          isFree: importedData.isFree ?? prev.isFree,
          displayOrder: importedData.displayOrder ?? prev.displayOrder,
          active: importedData.active ?? prev.active,
          themeData: importedData.themeData ?? prev.themeData,
        }));

        setSuccess('Theme data imported. Review changes and save.');
      } catch {
        setEditError('Failed to parse theme file. Please ensure it is valid JSON.');
      }
    };
    reader.readAsText(file);
    // Reset file input
    event.target.value = '';
  };

  // Export current edit state as JSON
  const handleExportFromEdit = () => {
    if (!editTheme) return;

    const exportData = {
      lookupKey: editTheme.lookupKey,
      name: editInput.name ?? editTheme.name,
      description: editInput.description ?? editTheme.description ?? '',
      category: editInput.category ?? editTheme.category,
      creditPrice: editInput.creditPrice ?? editTheme.creditPrice ?? 1,
      isFree: editInput.isFree ?? editTheme.isFree,
      displayOrder: editInput.displayOrder ?? editTheme.displayOrder,
      active: editInput.active ?? editTheme.active,
      themeData: editInput.themeData ?? editTheme.themeData ?? DEFAULT_THEME_DATA,
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `theme-${editTheme.lookupKey}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setSuccess(`Exported "${editInput.name ?? editTheme.name}" theme`);
  };

  const handleCopyTheme = (theme: ThemeWithStats) => {
    // Generate a unique lookup key
    const baseLookupKey = theme.lookupKey + '-copy';
    let lookupKey = baseLookupKey;
    let counter = 1;
    while (themes.some(t => t.lookupKey === lookupKey)) {
      lookupKey = `${baseLookupKey}-${counter}`;
      counter++;
    }

    setCreateInput({
      lookupKey,
      name: `${theme.name} (Copy)`,
      description: theme.description || '',
      category: theme.category,
      creditPrice: theme.creditPrice || 1,
      isFree: theme.isFree,
      displayOrder: theme.displayOrder + 1,
      active: false, // Start as inactive
      themeData: theme.themeData ? { ...theme.themeData } : { ...DEFAULT_THEME_DATA },
    });
    setCreateError(null);
    setCreateDialogOpen(true);
  };

  // Export theme as JSON file
  const handleExportTheme = (theme: ThemeWithStats) => {
    const exportData = {
      lookupKey: theme.lookupKey,
      name: theme.name,
      description: theme.description || '',
      category: theme.category,
      creditPrice: theme.creditPrice || 1,
      isFree: theme.isFree,
      displayOrder: theme.displayOrder,
      active: theme.active,
      themeData: theme.themeData || DEFAULT_THEME_DATA,
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `theme-${theme.lookupKey}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setSuccess(`Exported "${theme.name}" theme`);
  };

  // Import theme from JSON file
  const handleImportTheme = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target?.result as string);

        // Validate required fields
        if (!importedData.lookupKey || !importedData.name) {
          setError('Invalid theme file: missing lookupKey or name');
          return;
        }

        // Generate unique lookup key if it already exists
        let lookupKey = importedData.lookupKey;
        let counter = 1;
        while (themes.some(t => t.lookupKey === lookupKey)) {
          lookupKey = `${importedData.lookupKey}-${counter}`;
          counter++;
        }

        setCreateInput({
          lookupKey,
          name: counter > 1 ? `${importedData.name} (Imported)` : importedData.name,
          description: importedData.description || '',
          category: importedData.category || 'classic',
          creditPrice: importedData.creditPrice || 1,
          isFree: importedData.isFree || false,
          displayOrder: importedData.displayOrder || 100,
          active: false, // Always start as inactive when importing
          themeData: importedData.themeData || DEFAULT_THEME_DATA,
        });
        setCreateError(null);
        setCreateDialogOpen(true);
        setSuccess('Theme loaded from file. Review and save to create.');
      } catch {
        setError('Failed to parse theme file. Please ensure it is valid JSON.');
      }
    };
    reader.readAsText(file);
    // Reset file input
    event.target.value = '';
  };

  const getCategoryLabel = (category: string): string => {
    const found = THEME_CATEGORIES.find((c) => c.value === category);
    return found?.label || category;
  };

  // Filter and sort themes
  const filteredThemes = useMemo(() => {
    let result = [...themes];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (theme) =>
          theme.name.toLowerCase().includes(query) ||
          theme.lookupKey.toLowerCase().includes(query) ||
          (theme.description && theme.description.toLowerCase().includes(query))
      );
    }

    // Apply category filter
    if (categoryFilter !== 'all') {
      result = result.filter((theme) => theme.category === categoryFilter);
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter((theme) => (statusFilter === 'active' ? theme.active : !theme.active));
    }

    return result;
  }, [themes, searchQuery, categoryFilter, statusFilter]);

  // Paginated themes
  const paginatedThemes = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredThemes.slice(start, start + rowsPerPage);
  }, [filteredThemes, page, rowsPerPage]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchQuery, categoryFilter, statusFilter]);

  // Update theme data helper
  const updateThemeData = (updates: Partial<ThemeData>) => {
    setEditInput((prev) => ({
      ...prev,
      themeData: {
        ...(prev.themeData || DEFAULT_THEME_DATA),
        ...updates,
      },
    }));
  };

  // Live preview theme object
  const previewThemeObject = useMemo(() => {
    if (!editTheme || !editInput.themeData) return null;
    return {
      ...editTheme,
      ...editInput,
      themeData: editInput.themeData,
    } as ThemeWithStats;
  }, [editTheme, editInput]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ThemeIcon sx={{ fontSize: 32 }} />
          <Typography variant="h4">Theme Management</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            component="label"
          >
            Import Theme
            <input
              type="file"
              accept=".json"
              hidden
              onChange={handleImportTheme}
            />
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateOpen}>
            Add Theme
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search themes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value as ThemeCategory | 'all')}
            >
              <MenuItem value="all">All Categories</MenuItem>
              {THEME_CATEGORIES.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {filteredThemes.length} of {themes.length} themes
          </Typography>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 80 }}>Preview</TableCell>
              <TableCell>Name</TableCell>
              <TableCell align="center">Price</TableCell>
              <TableCell align="center">Owners</TableCell>
              <TableCell align="center">Boards</TableCell>
              <TableCell align="center">Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedThemes.map((theme) => (
              <TableRow key={theme.id} hover>
                <TableCell>
                  <Box
                    onClick={() => setPreviewTheme(theme)}
                    sx={{
                      cursor: 'pointer',
                      width: 50,
                      height: 34,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: theme.themeData?.cellBackground || '#fff',
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: '50%',
                        backgroundColor: theme.themeData?.homeTeamColor || '#1976d2',
                      }}
                    />
                    <Box
                      sx={{
                        width: '100%',
                        height: '50%',
                        display: 'flex',
                      }}
                    >
                      <Box
                        sx={{
                          width: '50%',
                          height: '100%',
                          backgroundColor: theme.themeData?.awayTeamColor || '#d32f2f',
                        }}
                      />
                      <Box
                        sx={{
                          width: '50%',
                          height: '100%',
                          backgroundColor: theme.themeData?.squareWinner || '#4caf50',
                        }}
                      />
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {theme.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {theme.lookupKey}
                    </Typography>
                    <Chip label={getCategoryLabel(theme.category)} size="small" variant="outlined" sx={{ height: 18, '& .MuiChip-label': { px: 0.75, py: 0, fontSize: '0.65rem' } }} />
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {theme.isFree ? (
                    <Chip label="Free" size="small" color="success" sx={{ height: 20 }} />
                  ) : (
                    <Typography variant="body2">
                      {theme.creditPrice || 1}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{theme.ownerCount || 0}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{theme.boardCount || 0}</Typography>
                </TableCell>
                <TableCell align="center">
                  {theme.active ? (
                    <CheckIcon color="success" fontSize="small" />
                  ) : (
                    <CloseIcon color="error" fontSize="small" />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Preview">
                    <IconButton size="small" onClick={() => setPreviewTheme(theme)}>
                      <PreviewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleEditClick(theme)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copy Theme">
                    <IconButton size="small" onClick={() => handleCopyTheme(theme)}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Export Theme">
                    <IconButton size="small" onClick={() => handleExportTheme(theme)}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={
                    (theme.ownerCount || 0) > 0 || (theme.boardCount || 0) > 0
                      ? `Cannot delete: ${theme.ownerCount || 0} owners, ${theme.boardCount || 0} boards`
                      : 'Delete Theme'
                  }>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(theme)}
                        disabled={(theme.ownerCount || 0) > 0 || (theme.boardCount || 0) > 0}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredThemes.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      {themes.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary">No themes found. Run database seeds to create initial themes.</Typography>
        </Box>
      )}

      {themes.length > 0 && filteredThemes.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">No themes match your filters.</Typography>
          <Button
            size="small"
            onClick={() => {
              setSearchQuery('');
              setCategoryFilter('all');
              setStatusFilter('all');
            }}
            sx={{ mt: 1 }}
          >
            Clear Filters
          </Button>
        </Box>
      )}

      {/* Preview Dialog with Full Board Mockup */}
      {previewTheme && (
        <ThemeFullPreview
          theme={{ ...previewTheme, owned: true }}
          open={!!previewTheme}
          onClose={() => setPreviewTheme(null)}
          actions={
            <>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  handleExportTheme(previewTheme);
                }}
              >
                Export
              </Button>
              <Button variant="outlined" onClick={() => setPreviewTheme(null)}>
                Close
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setPreviewTheme(null);
                  handleEditClick(previewTheme);
                }}
              >
                Edit Theme
              </Button>
            </>
          }
        />
      )}

      {/* Full Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Edit Theme: {editTheme?.name}</DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editError}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Left: Editor Controls */}
            <Grid size={{ xs: 12, md: 7 }}>
              <Tabs value={editTab} onChange={(_, v) => setEditTab(v)} variant="fullWidth" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tab icon={<EditIcon />} label="Basic" />
                <Tab icon={<ColorIcon />} label="Colors" />
                <Tab icon={<DecorationIcon />} label="Decorations" />
                <Tab icon={<AnimationIcon />} label="Styling" />
              </Tabs>

              {/* Basic Tab */}
              <TabPanel value={editTab} index={0}>
                <TextField
                  fullWidth
                  label="Name"
                  value={editInput.name || ''}
                  onChange={(e) => setEditInput({ ...editInput, name: e.target.value })}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Description"
                  value={editInput.description || ''}
                  onChange={(e) => setEditInput({ ...editInput, description: e.target.value })}
                  margin="normal"
                  multiline
                  rows={2}
                />
                <FormControl fullWidth margin="normal">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={editInput.category || ''}
                    label="Category"
                    onChange={(e) => setEditInput({ ...editInput, category: e.target.value as ThemeCategory })}
                  >
                    {THEME_CATEGORIES.map((cat) => (
                      <MenuItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <TextField
                    label="Credit Price"
                    type="number"
                    value={editInput.creditPrice ?? ''}
                    onChange={(e) => setEditInput({ ...editInput, creditPrice: parseInt(e.target.value) || 0 })}
                    disabled={editInput.isFree}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Display Order"
                    type="number"
                    value={editInput.displayOrder ?? ''}
                    onChange={(e) => setEditInput({ ...editInput, displayOrder: parseInt(e.target.value) || 0 })}
                    sx={{ flex: 1 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editInput.isFree ?? false}
                        onChange={(e) => setEditInput({ ...editInput, isFree: e.target.checked })}
                      />
                    }
                    label="Free Theme"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editInput.active ?? false}
                        onChange={(e) => setEditInput({ ...editInput, active: e.target.checked })}
                      />
                    }
                    label="Active"
                  />
                </Box>
              </TabPanel>

              {/* Colors Tab */}
              <TabPanel value={editTab} index={1}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Grid & Cell Colors
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <ColorInput
                      label="Grid Background"
                      value={editInput.themeData?.gridBackground || '#ffffff'}
                      onChange={(v) => updateThemeData({ gridBackground: v })}
                    />
                  </Grid>
                  <Grid size={6}>
                    <ColorInput
                      label="Cell Background"
                      value={editInput.themeData?.cellBackground || '#ffffff'}
                      onChange={(v) => updateThemeData({ cellBackground: v })}
                    />
                  </Grid>
                  <Grid size={6}>
                    <ColorInput
                      label="Cell Border"
                      value={editInput.themeData?.cellBorder || '#e0e0e0'}
                      onChange={(v) => updateThemeData({ cellBorder: v })}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Square State Colors
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <ColorInput
                      label="Unclaimed"
                      value={editInput.themeData?.squareUnclaimed || '#f5f5f5'}
                      onChange={(v) => updateThemeData({ squareUnclaimed: v })}
                    />
                  </Grid>
                  <Grid size={6}>
                    <ColorInput
                      label="Claimed (Self)"
                      value={editInput.themeData?.squareClaimedSelf || '#c8e6c9'}
                      onChange={(v) => updateThemeData({ squareClaimedSelf: v })}
                    />
                  </Grid>
                  <Grid size={6}>
                    <ColorInput
                      label="Claimed (Other)"
                      value={editInput.themeData?.squareClaimedOther || '#e3f2fd'}
                      onChange={(v) => updateThemeData({ squareClaimedOther: v })}
                    />
                  </Grid>
                  <Grid size={6}>
                    <ColorInput
                      label="Winner"
                      value={editInput.themeData?.squareWinner || '#fff9c4'}
                      onChange={(v) => updateThemeData({ squareWinner: v })}
                    />
                  </Grid>
                  <Grid size={6}>
                    <ColorInput
                      label="Selected"
                      value={editInput.themeData?.squareSelected || '#bbdefb'}
                      onChange={(v) => updateThemeData({ squareSelected: v })}
                    />
                  </Grid>
                  <Grid size={6}>
                    <ColorInput
                      label="Offline Player"
                      value={editInput.themeData?.squareOfflinePlayer || '#f3e5f5'}
                      onChange={(v) => updateThemeData({ squareOfflinePlayer: v })}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Team Colors
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <ColorInput
                      label="Home Team"
                      value={editInput.themeData?.homeTeamColor || '#1976d2'}
                      onChange={(v) => updateThemeData({ homeTeamColor: v })}
                    />
                  </Grid>
                  <Grid size={6}>
                    <ColorInput
                      label="Home Accent"
                      value={editInput.themeData?.homeTeamAccent || '#ffffff'}
                      onChange={(v) => updateThemeData({ homeTeamAccent: v })}
                    />
                  </Grid>
                  <Grid size={6}>
                    <ColorInput
                      label="Away Team"
                      value={editInput.themeData?.awayTeamColor || '#d32f2f'}
                      onChange={(v) => updateThemeData({ awayTeamColor: v })}
                    />
                  </Grid>
                  <Grid size={6}>
                    <ColorInput
                      label="Away Accent"
                      value={editInput.themeData?.awayTeamAccent || '#ffffff'}
                      onChange={(v) => updateThemeData({ awayTeamAccent: v })}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Winner Badge
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <ColorInput
                      label="Badge Background"
                      value={editInput.themeData?.winnerBadgeBackground || '#ffd700'}
                      onChange={(v) => updateThemeData({ winnerBadgeBackground: v })}
                    />
                  </Grid>
                  <Grid size={6}>
                    <ColorInput
                      label="Badge Text"
                      value={editInput.themeData?.winnerBadgeTextColor || '#000000'}
                      onChange={(v) => updateThemeData({ winnerBadgeTextColor: v })}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Decorations Tab */}
              <TabPanel value={editTab} index={2}>
                <DecorationEditor
                  decorations={editInput.themeData?.decorations || []}
                  onChange={(decorations) => updateThemeData({ decorations })}
                />
              </TabPanel>

              {/* Styling Tab */}
              <TabPanel value={editTab} index={3}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Animation
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Animation Effect</InputLabel>
                  <Select
                    value={editInput.themeData?.animation || 'none'}
                    label="Animation Effect"
                    onChange={(e) =>
                      updateThemeData({ animation: e.target.value as ThemeData['animation'] })
                    }
                  >
                    {ANIMATION_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Animations are shown on the live board view but not in exported images.
                </Typography>

                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Export Text Color Mode
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Text Color Mode</InputLabel>
                  <Select
                    value={editInput.themeData?.textColorMode || ''}
                    label="Text Color Mode"
                    onChange={(e) =>
                      updateThemeData({ textColorMode: e.target.value as ThemeData['textColorMode'] })
                    }
                  >
                    {TEXT_COLOR_MODE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
                  Controls the color of text in exported images. Use "Light" for themes with dark page backgrounds.
                </Typography>

                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Export Info Panel Background
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label="Info Panel Background (CSS color)"
                  placeholder="e.g., rgba(0,0,0,0.35)"
                  value={editInput.themeData?.infoPanelBackground || ''}
                  onChange={(e) => updateThemeData({ infoPanelBackground: e.target.value })}
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
                  Background color for the left side info panel in exported images. Use semi-transparent colors (rgba) for best results. Leave empty to use defaults based on text color mode.
                </Typography>

                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Page Background
                </Typography>
                <ColorInput
                  label="Background Color"
                  value={editInput.themeData?.pageBackground || ''}
                  onChange={(v) => updateThemeData({ pageBackground: v })}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Background Gradient (CSS)"
                  value={editInput.themeData?.pageGradient || ''}
                  onChange={(e) => updateThemeData({ pageGradient: e.target.value })}
                  placeholder="linear-gradient(180deg, #1a237e 0%, #0d47a1 100%)"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Background Image URL"
                  value={editInput.themeData?.backgroundImage || ''}
                  onChange={(e) => updateThemeData({ backgroundImage: e.target.value })}
                  placeholder="https://..."
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Background Overlay (RGBA)"
                  value={editInput.themeData?.backgroundOverlay || ''}
                  onChange={(e) => updateThemeData({ backgroundOverlay: e.target.value })}
                  placeholder="rgba(0, 0, 0, 0.3)"
                />

                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Cell Styling
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    size="small"
                    label="Border Width"
                    type="number"
                    value={editInput.themeData?.cellBorderWidth ?? 1}
                    onChange={(e) => updateThemeData({ cellBorderWidth: parseInt(e.target.value) || 1 })}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Border Radius"
                    type="number"
                    value={editInput.themeData?.cellBorderRadius ?? 0}
                    onChange={(e) => updateThemeData({ cellBorderRadius: parseInt(e.target.value) || 0 })}
                    sx={{ flex: 1 }}
                  />
                </Box>

                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Typography
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label="Font Family"
                  value={editInput.themeData?.fontFamily || ''}
                  onChange={(e) => updateThemeData({ fontFamily: e.target.value })}
                  placeholder='"Roboto", sans-serif'
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    size="small"
                    label="Cell Font Size"
                    value={editInput.themeData?.cellFontSize || '0.75rem'}
                    onChange={(e) => updateThemeData({ cellFontSize: e.target.value })}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Cell Font Weight"
                    value={editInput.themeData?.cellFontWeight || '500'}
                    onChange={(e) => updateThemeData({ cellFontWeight: e.target.value })}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Header Font Weight"
                    value={editInput.themeData?.headerFontWeight || '600'}
                    onChange={(e) => updateThemeData({ headerFontWeight: e.target.value })}
                    sx={{ flex: 1 }}
                  />
                </Box>
              </TabPanel>
            </Grid>

            {/* Right: Live Preview */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  height: '100%',
                  minHeight: 400,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Live Preview
                </Typography>
                {previewThemeObject && (
                  <Box sx={{ flex: 1, overflow: 'auto' }}>
                    <ThemeWrapper themeData={editInput.themeData} showAnimations={true}>
                      <Box sx={{ p: 2 }}>
                        <ThemePreview
                          theme={{ ...previewThemeObject, owned: true }}
                          size="large"
                          showOwnershipBadge={false}
                        />
                      </Box>
                    </ThemeWrapper>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadIcon />}
              component="label"
            >
              Import
              <input
                type="file"
                accept=".json"
                hidden
                onChange={handleImportToEdit}
              />
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExportFromEdit}
            >
              Export
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button
              variant="outlined"
              onClick={() => handleEditSave(false)}
              disabled={editLoading}
            >
              Save
            </Button>
            <Button
              variant="contained"
              onClick={() => handleEditSave(true)}
              disabled={editLoading}
              startIcon={editLoading && <CircularProgress size={16} />}
            >
              Save & Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Create Theme Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Theme</DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createError}
            </Alert>
          )}
          <TextField
            label="Lookup Key"
            value={createInput.lookupKey}
            onChange={(e) => setCreateInput({ ...createInput, lookupKey: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
            fullWidth
            margin="normal"
            required
            helperText="Unique identifier (lowercase letters, numbers, hyphens only)"
          />
          <TextField
            label="Name"
            value={createInput.name}
            onChange={(e) => setCreateInput({ ...createInput, name: e.target.value })}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Description"
            value={createInput.description}
            onChange={(e) => setCreateInput({ ...createInput, description: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            rows={2}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Category</InputLabel>
            <Select
              value={createInput.category}
              label="Category"
              onChange={(e) => setCreateInput({ ...createInput, category: e.target.value as ThemeCategory })}
            >
              {THEME_CATEGORIES.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField
              label="Credit Price"
              type="number"
              value={createInput.creditPrice}
              onChange={(e) => setCreateInput({ ...createInput, creditPrice: parseInt(e.target.value) || 0 })}
              sx={{ flex: 1 }}
              disabled={createInput.isFree}
            />
            <TextField
              label="Display Order"
              type="number"
              value={createInput.displayOrder}
              onChange={(e) => setCreateInput({ ...createInput, displayOrder: parseInt(e.target.value) || 0 })}
              sx={{ flex: 1 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={createInput.isFree}
                  onChange={(e) => setCreateInput({ ...createInput, isFree: e.target.checked, creditPrice: e.target.checked ? 0 : 1 })}
                />
              }
              label="Free Theme"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={createInput.active}
                  onChange={(e) => setCreateInput({ ...createInput, active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Theme styling can be configured after creation by editing the theme.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateSave}
            disabled={createLoading || !createInput.lookupKey || !createInput.name}
            startIcon={createLoading && <CircularProgress size={16} />}
          >
            Create Theme
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Theme</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the theme <strong>"{deleteTheme?.name}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteLoading}
            startIcon={deleteLoading && <CircularProgress size={16} />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar open={!!success} autoHideDuration={5000} onClose={() => setSuccess(null)} message={success} />
    </Box>
  );
}
